import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as CrmActions from '../../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../../state/crm/crm.selectors';

export interface ContactDetail {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone_number?: string;
  lead_source?: string;
  job_title?: string;
  linkedin?: string;
  personal_background?: string;
  full_name?: string;
  created_at: string;
  updated_at: string;
  company?: {
    id: number;
    name: string;
    url?: string;
  };
  company_contacts?: Array<{
    id: number;
    company_id: number;
    contact_id: number;
    is_default: number;
    company?: {
      id: number;
      name: string;
      url?: string;
    };
  }>;
}

@Component({
  selector: 'app-view-contact-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './view-contact-modal.component.html',
  styleUrl: './view-contact-modal.component.scss'
})
export class ViewContactModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() contactId: string | null = null;
  @Output() close = new EventEmitter<void>();

  contact: ContactDetail | null = null;
  loading: boolean = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] || changes['contactId']) {
      if (this.isVisible && this.contactId) {
        this.loadContactDetails();
      } else {
        this.resetModal();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadContactDetails(): void {
    if (!this.contactId) return;

    this.loading = true;
    this.error = null;
    this.contact = null;

    // Dispatch action to load contact details
    this.store.dispatch(CrmActions.loadContactDetail({ id: this.contactId }));

    // Subscribe to contact detail selector
    this.store.select(CrmSelectors.selectContactDetail)
      .pipe(takeUntil(this.destroy$))
      .subscribe(contact => {
        this.contact = contact;
        this.loading = false;
      });

    // Subscribe to loading state
    this.store.select(CrmSelectors.selectContactDetailLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loading = loading;
      });

    // Subscribe to error state
    this.store.select(CrmSelectors.selectContactDetailError)
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.error = error;
        if (error) {
          this.loading = false;
        }
      });
  }

  private resetModal(): void {
    this.contact = null;
    this.loading = false;
    this.error = null;
  }

  onClose(): void {
    this.resetModal();
    this.close.emit();
  }

  getInitials(name: string, surname: string): string {
    const firstInitial = name?.charAt(0)?.toUpperCase() || '';
    const lastInitial = surname?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial;
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getPrimaryCompany(): { id: number; name: string; url?: string } | null {
    if (!this.contact) return null;
    
    // First check for direct company property (fallback)
    if (this.contact.company) {
      return this.contact.company;
    }
    
    // Then check company_contacts array
    if (this.contact.company_contacts && this.contact.company_contacts.length > 0) {
      // Get the primary company (is_default = 1) or the first one
      const primaryCompany = this.contact.company_contacts.find(cc => cc.is_default === 1) 
                             || this.contact.company_contacts[0];
      return primaryCompany?.company || null;
    }
    
    return null;
  }
}